import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import enUS from '../i18n/locales/en-US.json';
import deDE from '../i18n/locales/de-DE.json';
import {
  DEFAULT_LOCALE,
  LANGUAGE_SELECTOR_LANGUAGES,
  detectInitialLocale,
  normalizeLocaleCode,
  resolveSupportedLocale,
} from '../lib/supportedLocales';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LanguageContext = createContext(null);

/** Frontend-bundled locale overrides merged after API. The repo is the source of
 * truth for platform-authored copy: bundling a locale here makes repo values win
 * over stale backend translation rows (same mechanism for de and en-US). */
const FRONTEND_LOCALE_BUNDLES = {
  de: deDE,
  'en-US': enUS,
};

const readInitialLocale = () => {
  const storedLocale = localStorage.getItem('locale');
  const hasVisited = Boolean(localStorage.getItem('hasVisited'));
  const browserLanguage = navigator.language || navigator.userLanguage || DEFAULT_LOCALE;
  const resolved = detectInitialLocale({ storedLocale, hasVisited, browserLanguage });

  if (storedLocale && normalizeLocaleCode(storedLocale) !== resolved) {
    localStorage.setItem('locale', resolved);
  }

  return resolved;
};

const _warnedKeys = new Set();

const humanizeTranslationKey = (key) => {
  if (!key || typeof key !== 'string') return '';
  const lastSegment = key.includes('.') ? key.split('.').pop() : key;
  const withSpaces = lastSegment.replace(/_/g, ' ').trim();
  if (!withSpaces) return key;
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => readInitialLocale());
  const [translations, setTranslations] = useState(enUS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem('hasVisited')) {
      localStorage.setItem('hasVisited', 'true');
    }
  }, []);

  const loadTranslations = useCallback(async (loc) => {
    const resolvedLocale = resolveSupportedLocale(loc);
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/translations/${resolvedLocale}`);
      const bundledLocale = FRONTEND_LOCALE_BUNDLES[resolvedLocale] || {};
      if (response.ok) {
        const data = await response.json();
        setTranslations({ ...enUS, ...(data.translations || {}), ...bundledLocale });
      } else {
        setTranslations({ ...enUS, ...bundledLocale });
      }
    } catch (error) {
      console.error('Failed to load translations:', error);
      const bundledLocale = FRONTEND_LOCALE_BUNDLES[resolvedLocale] || {};
      setTranslations({ ...enUS, ...bundledLocale });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTranslations(locale);
  }, [locale, loadTranslations]);

  const setLocale = useCallback(async (newLocale) => {
    const resolved = resolveSupportedLocale(newLocale);
    setLocaleState(resolved);
    localStorage.setItem('locale', resolved);

    const token = localStorage.getItem('token');
    if (token) {
      try {
        await fetch(`${API_URL}/api/users/me/locale?locale=${resolved}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error('Failed to update locale preference:', error);
      }
    }
  }, []);

  const t = useCallback((key, fallback) => {
    if (translations[key]) return translations[key];
    if (fallback) return fallback;
    if (process.env.NODE_ENV === 'development' && !_warnedKeys.has(key)) {
      _warnedKeys.add(key);
      console.warn(`[i18n] Missing key: "${key}" — add to en-US.json or pass a fallback`);
    }
    return humanizeTranslationKey(key);
  }, [translations]);

  return (
    <LanguageContext.Provider
      value={{
        locale,
        setLocale,
        t,
        translations,
        loading,
        languages: LANGUAGE_SELECTOR_LANGUAGES,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export function LanguageSelector({ className = '' }) {
  const { locale, setLocale, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const currentLang = languages.find((language) => language.code === locale) || languages[0];

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded transition-colors"
        title="Select Language"
        data-testid="language-selector"
      >
        <Globe className="w-3.5 h-3.5" />
        <span>{currentLang.flag}</span>
        <span className="hidden sm:inline">{currentLang.code.toUpperCase()}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden min-w-[140px]">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => {
                  setLocale(language.code);
                  setIsOpen(false);
                }}
                data-testid={`lang-option-${language.code}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  locale === language.code
                    ? 'bg-signal-blue/10 text-signal-blue'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <span>{language.flag}</span>
                <span>{language.name}</span>
                {locale === language.code && <span className="ml-auto text-signal-blue">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
